import { createFileRoute, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { Loader2 } from "lucide-react";
import { z } from "zod";
import { EarlyAccessNotice } from "@/components/EarlyAccessNotice";

export const Route = createFileRoute("/unsubscribe")({
  validateSearch: z.object({ token: z.string().optional() }),
  head: () => ({
    meta: [
      { title: "Unsubscribe - Smartasaurus" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: UnsubscribePage,
});

type State =
  | { kind: "loading" }
  | { kind: "ready" }
  | { kind: "already" }
  | { kind: "invalid" }
  | { kind: "success" }
  | { kind: "error"; message: string };

function UnsubscribePage() {
  const { token } = useSearch({ from: "/unsubscribe" });
  const [state, setState] = useState<State>({ kind: "loading" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setState({ kind: "invalid" });
      return;
    }
    fetch(`/email/unsubscribe?token=${encodeURIComponent(token)}`)
      .then(async (r) => {
        if (r.status === 404) return setState({ kind: "invalid" });
        const json = await r.json();
        if (json?.valid === false && json?.reason === "already_unsubscribed") {
          return setState({ kind: "already" });
        }
        if (json?.valid) return setState({ kind: "ready" });
        setState({ kind: "invalid" });
      })
      .catch(() => setState({ kind: "error", message: "Could not verify link." }));
  }, [token]);

  const confirm = async () => {
    if (!token) return;
    setSubmitting(true);
    try {
      const r = await fetch("/email/unsubscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });
      const json = await r.json();
      if (json?.success) return setState({ kind: "success" });
      if (json?.reason === "already_unsubscribed") return setState({ kind: "already" });
      setState({ kind: "error", message: json?.error ?? "Could not unsubscribe." });
    } catch (e: any) {
      setState({ kind: "error", message: e?.message ?? "Network error." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main id="main-content" className="mx-auto max-w-xl px-4 py-20 md:px-8 md:py-28">
        <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-primary">
          · Email preferences
        </div>
        <h1 className="mt-3 font-display text-4xl uppercase leading-[0.95] md:text-6xl">
          Unsubscribe
        </h1>

        <EarlyAccessNotice id="unsubscribe" className="mt-6" />

        <div className="mt-6 rounded-2xl border border-border bg-card p-6 md:p-8">
          {state.kind === "loading" && (
            <p className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Checking your link…
            </p>
          )}

          {state.kind === "invalid" && (
            <p className="text-muted-foreground">
              This unsubscribe link is invalid or has expired. If you're still getting
              emails you don't want, reply to one and we'll remove you manually.
            </p>
          )}

          {state.kind === "already" && (
            <p className="text-muted-foreground">
              You're already unsubscribed. You won't receive any more Smartasaurus emails.
            </p>
          )}

          {state.kind === "ready" && (
            <>
              <p className="text-muted-foreground">
                Confirm below and we'll stop sending Smartasaurus emails to this address.
              </p>
              <button
                type="button"
                onClick={confirm}
                disabled={submitting}
                className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-bold uppercase tracking-wider text-primary-foreground transition hover:brightness-110 disabled:opacity-60"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Confirm unsubscribe
              </button>
            </>
          )}

          {state.kind === "success" && (
            <p className="text-foreground">
              You're unsubscribed. Sorry to see you go - the rabbit hole stays open
              if you ever want back in.
            </p>
          )}

          {state.kind === "error" && (
            <p className="text-destructive">{state.message}</p>
          )}
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
